<template>
	<div class="product-list">
		<!-- 提示信息 -->
		<div class="tips-text" v-if="orderProductList.length > 0">
			以下商品适用该券，已选{{ checkedCount }}件，小计￥{{ totalAmount }}，
			<span v-if="totalAmount >= item.satisfyAmount">已满足适用条件</span>
			<span v-else class="imbalance-text">
				还差
				<span class="price">￥{{ imbalance }}</span>
				可用
			</span>
		</div>
		<div class="tips-text" v-else>购物车里商品不适用该券，赶快领完券去凑单吧！</div>

		<!-- 商品列表 -->
		<div class="product-items">
			<div v-for="(list, index) in displayProductList" :key="'product' + index" class="product-item">
				<div class="image-wrapper">
					<NuxtImg
						:src="list.imageUrl || '/staticSeo/images/placeholder.png'"
						:alt="list.productNameMove"
						width="64"
						height="64"
						format="webp"
						class="product-image"
						@error="handleImageError"
					/>

					<van-checkbox
						v-model="list.checked"
						:disabled="!item.userReceiveId"
						icon-size="16px"
						shape="square"
						class="product-checkbox"
						@click="handleProductClick(list, item)"
					/>

					<!-- 区域标识 -->
					<div v-if="showAreaName(list)" class="area-tag" title="{{ list.areaName }}">
						{{ list.areaName }}
					</div>

					<div class="quantity-tag">x {{ list.purchaseQuantity }}</div>
				</div>

				<div class="product-info">
					<div class="product-price">{{ formatPriceDisplay(list.productPrice) }}</div>
					<div class="product-name ellipsis">{{ list.productNameMove }}</div>
					<div class="product-spec ellipsis">规格：{{ list.specificationsMove }}</div>
				</div>
			</div>
		</div>

		<!-- 展开更多按钮 -->
		<div v-if="orderProductList.length > displayLength" class="expand-btn" @click="expandList">
			<van-icon name="arrow-double-down" size="20" />
		</div>
	</div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { math } from '@/utils/business/utilsMath.js';
import { showToast } from 'vant';

const props = defineProps({
	item: {
		type: Object,
		default: () => ({}),
	},
	checkedIds: {
		type: Array,
		default: () => [],
	},
	receives: {
		type: Array,
		default: () => [],
	},
	couponActivityVo: {
		type: Array,
		default: () => [],
	},
});

const emit = defineEmits(['choose', 'changeCoupon']);

const displayLength = ref(4); // 默认显示 4 个
const OPEN_LEN = 12; // 每次展开数量

// 处理图片错误
const handleImageError = e => {
	e.target.src = '/staticSeo/images/placeholder.png';
};

const { formatPrice } = usePriceFormat();
// 格式化价格显示（智能添加￥符号）
const formatPriceDisplay = price => {
	const formatted = formatPrice(price);

	// 如果是数字或可以转换为数字的字符串，添加￥符号
	if (typeof formatted === 'number' || (!isNaN(parseFloat(formatted)) && typeof formatted === 'string')) {
		return `￥${formatted}`;
	}

	// 否则直接返回文本（如"登录可见"、"可询价"等）
	return formatted;
};

// 订单商品列表（带选中状态）
const orderProductList = computed(() => {
	return (props.item.orderProductList || []).map(list => ({
		...list,
		checked: props.checkedIds.includes(list.productId + (list.agreementPriceId || '')),
	}));
});

// 显示的商品列表（分页）
const displayProductList = computed(() => {
	return orderProductList.value.slice(0, displayLength.value);
});

// 选中的商品列表
const checkedList = computed(() => {
	return orderProductList.value.filter(item => item.checked);
});

// 选中数量
const checkedCount = computed(() => {
	return orderProductList.value.some(list => list.checkSign === 1) ? checkedList.value.length : 0;
});

// 总金额
const totalAmount = computed(() => {
	let total = 0;
	checkedList.value.forEach(item => {
		total = math.add(total, math.multiply(item.productPrice, item.purchaseQuantity));
	});
	return orderProductList.value.some(list => list.checkSign === 1) ? total : 0;
});

// 差额
const imbalance = computed(() => {
	return math.subtract(props.item.satisfyAmount, totalAmount.value);
});

// 是否显示区域名称
const showAreaName = list => {
	return list.areaName && orderProductList.value.filter(item => item.productId === list.productId).length > 1;
};

// 点击商品复选框
const handleProductClick = (list, item) => {
	if (!item.userReceiveId) {
		showToast({
			message: '请先领取优惠券',
			icon: 'none',
			duration: 2000,
		});
		return;
	}

	list.checked = !list.checked;

	// 更新选中 ID 列表
	const productId = list.productId + (list.agreementPriceId || '');
	const index = props.checkedIds.indexOf(productId);

	if (index > -1) {
		// 取消选中
		props.checkedIds.splice(index, 1);
	} else {
		// 添加选中
		props.checkedIds.push(productId);
	}

	// 选择优惠券逻辑
	const types = props.receives.map(receive => receive.productRangeType);

	props.receives.forEach(receive => {
		if (receive.productRangeType === item.productRangeType) {
			receive.userReceiveId = item.userReceiveId;
		}
	});

	if (!types.includes(item.productRangeType)) {
		props.receives.push(item);
	}

	emit('choose');
	emit(
		'changeCoupon',
		props.receives.map(receive => receive.userReceiveId)
	);
};

// 展开更多
const expandList = () => {
	displayLength.value = displayLength.value + OPEN_LEN;
};
</script>

<style scoped lang="scss">
.product-list {
	width: 95%;
	margin: 16px auto;
	display: flex;
	flex-wrap: wrap;
	border-top: 3px dotted #ddd;

	.tips-text {
		width: 100%;
		color: #909399;
		font-size: 14px;
		line-height: 24px;
		margin-bottom: 8px;

		.imbalance-text {
			.price {
				text-align: left;
				font-size: 16px;
				padding: 0 8px;
				color: #f56c6c;
			}
		}
	}

	.product-items {
		width: 100%;
		display: flex;
		flex-wrap: wrap;
		gap: 8px;
	}

	.product-item {
		width: calc(48% - 4px);
		margin-top: 10px;
		padding: 8px;
		text-align: center;
		background: #ffffff;
		box-shadow: 0 3px 5px rgba(192, 196, 204, 0.53);
		border-radius: 4px;
		display: flex;
		gap: 8px;

		&:nth-of-type(even) {
			margin-right: 0;
		}
	}

	.image-wrapper {
		width: 64px;
		position: relative;
		border-radius: 11px;

		.product-image {
			width: 64px;
			height: 64px;
			object-fit: cover;
			border-radius: 11px;
		}

		.product-checkbox {
			position: absolute;
			top: -10px;
			right: -13px;
			z-index: 10;

			:deep(.van-checkbox__icon) {
				width: 16px;
				height: 16px;
			}
		}

		.area-tag {
			position: absolute;
			bottom: 22px;
			left: 0;
			width: 64px;
			padding: 2px 4px;
			background: rgba(127, 144, 245, 0.8);
			color: #fff;
			font-size: 10px;
			text-align: center;
			border-radius: 0 0 4px 4px;
			overflow: hidden;
			white-space: nowrap;
		}

		.quantity-tag {
			position: absolute;
			bottom: 0;
			left: 0;
			width: 100%;
			height: 21px;
			line-height: 21px;
			background: rgba(0, 0, 0, 0.3);
			text-align: center;
			border-radius: 0 0 11px 11px;
			color: #fff;
			font-size: 12px;
		}
	}

	.product-info {
		flex: 1;
		text-align: left;
		padding-left: 8px;

		.product-price {
			display: block;
			text-align: left;
			line-height: 23px;
			color: #f56c6c;
			font-size: 16px;
			font-weight: 500;
		}

		.product-name,
		.product-spec {
			display: block;
			text-align: left;
			line-height: 23px;
			font-size: 13px;
			color: #303133;
		}

		.product-spec {
			color: #909399;
		}
	}

	.expand-btn {
		margin: 8px auto 0;
		text-align: center;
		cursor: pointer;
		color: #909399;

		i {
			font-size: 21px;
		}
	}
}

.ellipsis {
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}
</style>
